"use client";

import type { RefObject } from "react";
import {
  CHART_BLUE,
  CHART_GRID,
  CHART_INK,
  CHART_MUTED,
  CHART_RED,
} from "@/components/tools/stats-clinical/chart";
import { nomogramPositions } from "@/lib/tool-kit/stats-clinical/post-test";

/*
 * A Fagan nomogram drawn as an SVG: pre-test probability on the left (inverted), the
 * likelihood ratio in the middle, post-test probability on the right. A straight line from
 * the pre-test point through the likelihood ratio lands on the post-test probability.
 * Colours are hex attributes so the downloaded SVG and PNG match the page.
 */

const WIDTH = 560;
const HEIGHT = 540;
const LEFT_X = 120;
const MIDDLE_X = 280;
const RIGHT_X = 440;
const CENTRE_Y = 270;
const UNIT = 33; // pixels per natural-log unit of odds
const FONT = "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";

const PROBABILITIES = [
  0.1, 0.2, 0.5, 1, 2, 5, 10, 20, 30, 40, 50, 60, 70, 80, 90, 95, 98, 99, 99.5,
  99.8, 99.9,
];
const RATIOS = [
  0.001, 0.002, 0.005, 0.01, 0.02, 0.05, 0.1, 0.2, 0.5, 1, 2, 5, 10, 20, 50,
  100, 200, 500, 1000,
];
const LABELLED_RATIOS = new Set([
  0.001, 0.01, 0.1, 0.5, 1, 2, 5, 10, 100, 1000,
]);

function logOdds(percent: number): number {
  const p = percent / 100;
  return Math.log(p / (1 - p));
}

export function FaganNomogram({
  preTestProbability,
  likelihoodRatio,
  postTestProbability,
  labels,
  formatNumber,
  svgRef,
  description,
}: {
  preTestProbability: number;
  likelihoodRatio: number;
  postTestProbability: number;
  labels: { pre: string; ratio: string; post: string };
  formatNumber: (value: number, maximumFractionDigits?: number) => string;
  svgRef: RefObject<SVGSVGElement | null>;
  description: string;
}) {
  const y = (units: number) => CENTRE_Y - units * UNIT;
  const positions = nomogramPositions(preTestProbability, likelihoodRatio);
  const ratioInRange = likelihoodRatio >= 0.001 && likelihoodRatio <= 1000;
  return (
    <svg
      aria-label={description}
      className="h-auto w-full max-w-xl rounded-lg border border-border"
      height={HEIGHT}
      ref={svgRef}
      role="img"
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      width={WIDTH}
    >
      <title>{description}</title>
      <rect fill="#ffffff" height={HEIGHT} width={WIDTH} x={0} y={0} />
      {[LEFT_X, MIDDLE_X, RIGHT_X].map((x) => (
        <line
          key={x}
          stroke={CHART_MUTED}
          x1={x}
          x2={x}
          y1={y(-6.95)}
          y2={y(6.95)}
        />
      ))}
      {PROBABILITIES.map((p) => (
        <g key={`p${p}`}>
          <line
            stroke={CHART_MUTED}
            x1={LEFT_X}
            x2={LEFT_X + 8}
            y1={y(-logOdds(p))}
            y2={y(-logOdds(p))}
          />
          <text
            fill={CHART_INK}
            fontFamily={FONT}
            fontSize={10}
            textAnchor="end"
            x={LEFT_X - 6}
            y={y(-logOdds(p)) + 3.5}
          >
            {formatNumber(p, 1)}
          </text>
          <line
            stroke={CHART_MUTED}
            x1={RIGHT_X - 8}
            x2={RIGHT_X}
            y1={y(logOdds(p))}
            y2={y(logOdds(p))}
          />
          <text
            fill={CHART_INK}
            fontFamily={FONT}
            fontSize={10}
            textAnchor="start"
            x={RIGHT_X + 6}
            y={y(logOdds(p)) + 3.5}
          >
            {formatNumber(p, 1)}
          </text>
        </g>
      ))}
      {RATIOS.map((r) => (
        <g key={`r${r}`}>
          <line
            stroke={CHART_MUTED}
            x1={MIDDLE_X - 6}
            x2={MIDDLE_X + 6}
            y1={y(Math.log(r) / 2)}
            y2={y(Math.log(r) / 2)}
          />
          {LABELLED_RATIOS.has(r) ? (
            <text
              fill={CHART_INK}
              fontFamily={FONT}
              fontSize={10}
              textAnchor="start"
              x={MIDDLE_X + 10}
              y={y(Math.log(r) / 2) + 3.5}
            >
              {formatNumber(r, 3)}
            </text>
          ) : null}
        </g>
      ))}
      <line stroke={CHART_GRID} x1={LEFT_X} x2={RIGHT_X} y1={y(0)} y2={y(0)} />
      <line
        stroke={CHART_RED}
        strokeWidth={2}
        x1={LEFT_X}
        x2={RIGHT_X}
        y1={y(positions.pre)}
        y2={y(positions.post)}
      />
      {[
        { x: LEFT_X, value: positions.pre },
        { x: MIDDLE_X, value: ratioInRange ? positions.middle : Number.NaN },
        { x: RIGHT_X, value: positions.post },
      ]
        .filter((dot) => Number.isFinite(dot.value))
        .map((dot) => (
          <circle
            cx={dot.x}
            cy={y(dot.value)}
            fill={CHART_RED}
            key={dot.x}
            r={5}
            stroke="#ffffff"
            strokeWidth={1.5}
          />
        ))}
      <text
        fill={CHART_BLUE}
        fontFamily={FONT}
        fontSize={12}
        fontWeight={600}
        textAnchor="end"
        x={LEFT_X - 34}
        y={y(positions.pre) - 8}
      >
        {formatNumber(preTestProbability * 100, 1)}%
      </text>
      <text
        fill={CHART_BLUE}
        fontFamily={FONT}
        fontSize={12}
        fontWeight={600}
        textAnchor="start"
        x={RIGHT_X + 30}
        y={y(positions.post) - 8}
      >
        {formatNumber(postTestProbability * 100, 1)}%
      </text>
      {[
        { x: LEFT_X, text: labels.pre },
        { x: MIDDLE_X, text: labels.ratio },
        { x: RIGHT_X, text: labels.post },
      ].map((title) => (
        <text
          fill={CHART_INK}
          fontFamily={FONT}
          fontSize={12}
          key={title.x}
          textAnchor="middle"
          x={title.x}
          y={HEIGHT - 14}
        >
          {title.text}
        </text>
      ))}
    </svg>
  );
}
